import { Injectable, type Signal, signal } from '@angular/core';
import type { NavItemConfig } from '../models/nav-item.model';

/**
 * Rail items that exist only while a particular screen is open (#1530: the Hearing item while a
 * hearing detail page is open), keyed by the registering source so each source replaces or
 * removes only its own items.
 *
 * This registry has no dependencies of its own so a feature shell can write to it from any
 * injector; the navigation service is the read side and folds the items into the rail through the
 * same access gate as the static items.
 */
@Injectable({ providedIn: 'root' })
export class NavContextItemRegistry {
  private readonly itemsBySource = signal<
    Readonly<Record<string, readonly NavItemConfig[]>>
  >({});

  /** Registered items keyed by source. */
  readonly items: Signal<Readonly<Record<string, readonly NavItemConfig[]>>> =
    this.itemsBySource.asReadonly();

  set(sourceKey: string, items: readonly NavItemConfig[]): void {
    this.itemsBySource.update((current) => ({
      ...current,
      [sourceKey]: [...items],
    }));
  }

  clear(sourceKey: string): void {
    this.itemsBySource.update((current) => {
      if (!(sourceKey in current)) {
        return current;
      }

      const rest = { ...current };
      delete rest[sourceKey];
      return rest;
    });
  }
}
